import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import { 
  TrendingUp, Plus, Search, Clock, CheckCircle, XCircle, AlertTriangle, 
  Eye, Send, Filter, RefreshCw, ArrowUpRight, BarChart3, Percent
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const VariationsPage = () => {
  const { token, user } = useAuth();
  const [variations, setVariations] = useState([]);
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedVariation, setSelectedVariation] = useState(null);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showApprovalDialog, setShowApprovalDialog] = useState(false);
  const [jobs, setJobs] = useState([]);

  const fetchData = async () => {
    try {
      const [variationsRes, dashboardRes, jobsRes] = await Promise.all([
        fetch(`${API_URL}/api/variations/`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/variations/dashboard`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/ppm-core/jobs`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ]);

      if (variationsRes.ok) setVariations(await variationsRes.json());
      if (dashboardRes.ok) setDashboard(await dashboardRes.json());
      if (jobsRes.ok) setJobs(await jobsRes.json());
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) fetchData();
  }, [token]);

  const getStatusBadge = (status) => {
    const styles = {
      draft: 'bg-gray-100 text-gray-700',
      submitted: 'bg-blue-100 text-blue-700',
      approved: 'bg-green-100 text-green-700',
      rejected: 'bg-red-100 text-red-700',
      partially_approved: 'bg-yellow-100 text-yellow-700'
    };
    return <Badge className={styles[status] || 'bg-gray-100'}>{status?.replace('_', ' ')}</Badge>;
  };

  const getTypeBadge = (type) => {
    const styles = {
      additional_works: 'bg-purple-100 text-purple-700',
      scope_change: 'bg-blue-100 text-blue-700',
      price_increase: 'bg-orange-100 text-orange-700',
      time_extension: 'bg-cyan-100 text-cyan-700',
      unforeseen_conditions: 'bg-amber-100 text-amber-700',
      client_request: 'bg-indigo-100 text-indigo-700'
    };
    return <Badge className={styles[type] || 'bg-gray-100'}>{type?.replace('_', ' ')}</Badge>;
  };

  const filteredVariations = variations.filter(v => {
    if (filter !== 'all' && v.status !== filter) return false;
    if (searchQuery && !v.variation_number?.toLowerCase().includes(searchQuery.toLowerCase()) &&
        !v.title?.toLowerCase().includes(searchQuery.toLowerCase())) return false;
    return true;
  });

  const handleSubmitVariation = async (variationId) => {
    try {
      const res = await fetch(`${API_URL}/api/variations/${variationId}/submit`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        toast.success(data.message);
        fetchData();
      } else {
        toast.error('Failed to submit variation');
      }
    } catch (error) {
      toast.error('Error submitting variation');
    }
  };

  const handleApproval = async (variationId, decision, notes, approvedAmount) => {
    try {
      const res = await fetch(`${API_URL}/api/variations/${variationId}/approve`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ 
          decision, 
          notes,
          approved_amount: decision === 'partial_approve' ? approvedAmount : undefined
        })
      });
      if (res.ok) {
        const data = await res.json();
        toast.success(data.message);
        setShowApprovalDialog(false);
        setSelectedVariation(null);
        fetchData();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to process approval');
      }
    } catch (error) {
      toast.error('Error processing approval');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#1F7A78]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-xl sm:text-2xl font-semibold text-gray-900">Variations & Commercial Control</h1>
          <p className="text-gray-500 text-xs sm:text-sm mt-1">Cost uplift approvals and budget tracking</p>
        </div>
        <Button onClick={() => setShowCreateDialog(true)} className="bg-[#1F7A78] hover:bg-[#005f5f] w-full sm:w-auto">
          <Plus className="w-4 h-4 mr-2" /> New Variation
        </Button>
      </div>

      {/* KPI Cards */}
      {dashboard && (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 md:gap-4">
          <Card className="cursor-pointer hover:border-gray-300" onClick={() => setFilter('draft')}>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Draft</p>
                  <p className="text-2xl font-semibold">{dashboard.counts?.draft || 0}</p>
                </div>
                <TrendingUp className="w-8 h-8 text-gray-400" />
              </div>
            </CardContent>
          </Card>
          <Card className="cursor-pointer hover:border-blue-300" onClick={() => setFilter('submitted')}>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Pending</p>
                  <p className="text-2xl font-semibold text-blue-600">{dashboard.counts?.pending_approval || 0}</p>
                </div>
                <Clock className="w-8 h-8 text-blue-400" />
              </div>
            </CardContent>
          </Card>
          <Card className="cursor-pointer hover:border-green-300" onClick={() => setFilter('approved')}>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Approved</p>
                  <p className="text-2xl font-semibold text-green-600">{dashboard.counts?.approved || 0}</p>
                </div>
                <CheckCircle className="w-8 h-8 text-green-400" />
              </div>
            </CardContent>
          </Card>
          <Card className="cursor-pointer hover:border-red-300" onClick={() => setFilter('rejected')}>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Rejected</p>
                  <p className="text-2xl font-semibold text-red-600">{dashboard.counts?.rejected || 0}</p>
                </div>
                <XCircle className="w-8 h-8 text-red-400" />
              </div>
            </CardContent>
          </Card>
          <Card className="border-orange-200">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Flagged</p>
                  <p className="text-2xl font-semibold text-orange-600">{dashboard.counts?.flagged || 0}</p>
                </div>
                <AlertTriangle className="w-8 h-8 text-orange-400" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Approved Value</p>
                  <p className="text-xl font-semibold text-[#1F7A78]">£{(dashboard.values?.approved_total || 0).toLocaleString()}</p>
                </div>
                <ArrowUpRight className="w-8 h-8 text-[#1F7A78]" />
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Thresholds Info */}
      {dashboard?.thresholds && (
        <Card className="bg-gradient-to-r from-gray-50 to-blue-50/30">
          <CardContent className="p-3 md:p-4">
            <div className="flex items-center gap-2 mb-2">
              <BarChart3 className="w-5 h-5 text-[#1F7A78]" />
              <span className="font-medium text-sm md:text-base">Approval Thresholds</span>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 md:gap-4 text-xs md:text-sm">
              <div>
                <span className="text-gray-500">Auto:</span>
                <span className="ml-1 md:ml-2 font-medium">≤{dashboard.thresholds.auto_approve_percent}%</span>
              </div>
              <div>
                <span className="text-gray-500">Manager:</span>
                <span className="ml-1 md:ml-2 font-medium">≤{dashboard.thresholds.manager_approve_percent}%</span>
              </div>
              <div>
                <span className="text-gray-500">Director:</span>
                <span className="ml-1 md:ml-2 font-medium">≤{dashboard.thresholds.director_approve_percent}%</span>
              </div>
              <div>
                <span className="text-gray-500">Cumulative:</span>
                <span className="ml-1 md:ml-2 font-medium text-orange-600">&gt;{dashboard.thresholds.max_cumulative_percent}%</span>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Filters */}
      <Card>
        <CardContent className="p-3 md:p-4">
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 items-stretch sm:items-center">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                placeholder="Search variations..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
            <div className="flex gap-2">
              <Select value={filter} onValueChange={setFilter}>
                <SelectTrigger className="w-full sm:w-[180px]">
                  <Filter className="w-4 h-4 mr-2" />
                  <SelectValue placeholder="Filter by status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Variations</SelectItem>
                  <SelectItem value="draft">Draft</SelectItem>
                  <SelectItem value="submitted">Submitted</SelectItem>
                  <SelectItem value="approved">Approved</SelectItem>
                  <SelectItem value="partially_approved">Partially Approved</SelectItem>
                  <SelectItem value="rejected">Rejected</SelectItem>
                </SelectContent>
              </Select>
              <Button variant="outline" onClick={fetchData} className="shrink-0">
                <RefreshCw className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Variations Table/Cards */}
      <Card>
        <CardHeader className="pb-2 md:pb-4">
          <CardTitle className="text-lg md:text-xl">Variations ({filteredVariations.length})</CardTitle>
        </CardHeader>
        <CardContent>
          {filteredVariations.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <TrendingUp className="w-12 h-12 mx-auto mb-4 opacity-30" />
              <p>No variations found</p>
            </div>
          ) : (
            <>
              {/* Mobile Card View */}
              <div className="md:hidden space-y-3">
                {filteredVariations.map(variation => (
                  <div key={variation.id} className="border rounded-lg p-4 space-y-3">
                    <div className="flex items-start justify-between">
                      <div>
                        <p className="font-mono text-sm font-medium">{variation.variation_number}</p>
                        <p className="text-sm text-gray-600 mt-1">{variation.title}</p>
                      </div>
                      {getStatusBadge(variation.status)}
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                      {getTypeBadge(variation.variation_type)}
                      {variation.cumulative_flag && (
                        <Badge className="bg-orange-100 text-orange-700">
                          <AlertTriangle className="w-3 h-3 mr-1" />
                          {variation.cumulative_percent}%
                        </Badge>
                      )}
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className={variation.variation_percent > 10 ? 'text-red-600 font-medium' : ''}>
                        {variation.variation_percent}%
                      </span>
                      <span className="font-semibold text-orange-600">+£{(variation.variation_amount || 0).toLocaleString()}</span>
                    </div>
                    <div className="flex items-center justify-between pt-2 border-t">
                      <span className="text-xs text-gray-500">{new Date(variation.created_at).toLocaleDateString()}</span>
                      <div className="flex gap-2">
                        <Button variant="ghost" size="sm" onClick={() => setSelectedVariation(variation)}>
                          <Eye className="w-4 h-4" />
                        </Button>
                        {variation.status === 'draft' && (
                          <Button variant="ghost" size="sm" onClick={() => handleSubmitVariation(variation.id)} className="text-blue-600">
                            <Send className="w-4 h-4" />
                          </Button>
                        )}
                        {variation.status === 'submitted' && ['admin', 'manager', 'director'].includes(user?.role) && (
                          <Button variant="ghost" size="sm" onClick={() => { setSelectedVariation(variation); setShowApprovalDialog(true); }} className="text-green-600">
                            <CheckCircle className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Desktop Table View */}
              <div className="hidden md:block overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="text-left p-3 text-sm font-medium text-gray-600">Variation #</th>
                    <th className="text-left p-3 text-sm font-medium text-gray-600">Title</th>
                    <th className="text-left p-3 text-sm font-medium text-gray-600">Type</th>
                    <th className="text-left p-3 text-sm font-medium text-gray-600">Status</th>
                    <th className="text-right p-3 text-sm font-medium text-gray-600">Amount</th>
                    <th className="text-right p-3 text-sm font-medium text-gray-600">%</th>
                    <th className="text-center p-3 text-sm font-medium text-gray-600">Flag</th>
                    <th className="text-right p-3 text-sm font-medium text-gray-600">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {filteredVariations.map(variation => (
                    <tr key={variation.id} className="hover:bg-gray-50">
                      <td className="p-3 font-mono text-sm">{variation.variation_number}</td>
                      <td className="p-3">{variation.title}</td>
                      <td className="p-3">{getTypeBadge(variation.variation_type)}</td>
                      <td className="p-3">{getStatusBadge(variation.status)}</td>
                      <td className="p-3 text-right font-semibold">
                        £{(variation.variation_amount || 0).toLocaleString()}
                      </td>
                      <td className="p-3 text-right">
                        <span className={variation.variation_percent > 10 ? 'text-red-600 font-medium' : ''}>
                          {variation.variation_percent}%
                        </span>
                      </td>
                      <td className="p-3 text-center">
                        {variation.cumulative_flag && (
                          <Badge className="bg-orange-100 text-orange-700">
                            <AlertTriangle className="w-3 h-3 mr-1" />
                            {variation.cumulative_percent}%
                          </Badge>
                        )}
                      </td>
                      <td className="p-3 text-right">
                        <div className="flex justify-end gap-2">
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => setSelectedVariation(variation)}
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                          {variation.status === 'draft' && (
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => handleSubmitVariation(variation.id)}
                              className="text-blue-600"
                            >
                              <Send className="w-4 h-4" />
                            </Button>
                          )}
                          {variation.status === 'submitted' && 
                           ['admin', 'manager', 'director'].includes(user?.role) && (
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => { setSelectedVariation(variation); setShowApprovalDialog(true); }}
                              className="text-green-600"
                            >
                              <CheckCircle className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Variation Details Dialog */}
      <Dialog open={!!selectedVariation && !showApprovalDialog} onOpenChange={() => setSelectedVariation(null)}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Variation Details - {selectedVariation?.variation_number}</DialogTitle>
          </DialogHeader>
          {selectedVariation && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-500">Status</p>
                  <p>{getStatusBadge(selectedVariation.status)}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Type</p>
                  <p>{getTypeBadge(selectedVariation.variation_type)}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Title</p>
                  <p className="font-medium">{selectedVariation.title}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Approval Level Required</p>
                  <p className="capitalize">{selectedVariation.approval_level_required}</p>
                </div>
              </div>

              <div>
                <p className="text-sm text-gray-500 mb-1">Reason</p>
                <p className="text-sm">{selectedVariation.reason}</p>
              </div>

              <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                <div className="flex justify-between">
                  <span>Original Contract Value</span>
                  <span>£{(selectedVariation.original_contract_value || 0).toLocaleString()}</span>
                </div>
                <div className="flex justify-between font-medium text-orange-600">
                  <span>Variation Amount</span>
                  <span>+£{(selectedVariation.variation_amount || 0).toLocaleString()} ({selectedVariation.variation_percent}%)</span>
                </div>
                <div className="flex justify-between font-bold text-lg border-t pt-2">
                  <span>New Contract Value</span>
                  <span className="text-[#1F7A78]">£{(selectedVariation.new_contract_value || 0).toLocaleString()}</span>
                </div>
              </div>

              {selectedVariation.cumulative_flag && (
                <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                  <div className="flex items-center gap-2 text-orange-700">
                    <AlertTriangle className="w-5 h-5" />
                    <span className="font-medium">Cumulative Threshold Exceeded</span>
                  </div>
                  <p className="text-sm text-orange-600 mt-1">
                    Total cumulative variations: £{(selectedVariation.cumulative_variations || 0).toLocaleString()} 
                    ({selectedVariation.cumulative_percent}%)
                  </p>
                </div>
              )}

              {selectedVariation.approval_history?.length > 0 && (
                <div>
                  <p className="text-sm font-medium mb-2">Approval History</p>
                  <div className="space-y-2">
                    {selectedVariation.approval_history.map((entry, idx) => (
                      <div key={idx} className="flex items-start gap-2 text-sm border-l-2 border-gray-200 pl-3">
                        <div>
                          <span className="font-medium capitalize">{entry.action?.replace('_', ' ')}</span>
                          <span className="text-gray-500"> by {entry.by}</span>
                          <span className="text-gray-400"> - {new Date(entry.at).toLocaleString()}</span>
                          {entry.notes && <p className="text-gray-600 mt-1">{entry.notes}</p>}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Approval Dialog */}
      <Dialog open={showApprovalDialog} onOpenChange={setShowApprovalDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Approve/Reject Variation</DialogTitle>
          </DialogHeader>
          {selectedVariation && (
            <VariationApprovalForm 
              variation={selectedVariation}
              onApprove={(decision, notes, amount) => handleApproval(selectedVariation.id, decision, notes, amount)}
              onCancel={() => { setShowApprovalDialog(false); setSelectedVariation(null); }}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Create Variation Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Create New Variation</DialogTitle>
          </DialogHeader>
          <CreateVariationForm 
            jobs={jobs}
            token={token}
            onSuccess={() => { setShowCreateDialog(false); fetchData(); }}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Approval Form Component
const VariationApprovalForm = ({ variation, onApprove, onCancel }) => {
  const [notes, setNotes] = useState('');
  const [partialAmount, setPartialAmount] = useState(variation.variation_amount);

  return (
    <div className="space-y-4">
      <div className="bg-gray-50 rounded-lg p-4">
        <p className="font-medium">{variation.variation_number}</p>
        <p className="text-sm text-gray-500">{variation.title}</p>
        <div className="mt-2 space-y-1">
          <p className="text-sm">Original: £{(variation.original_contract_value || 0).toLocaleString()}</p>
          <p className="text-lg font-bold text-orange-600">+£{(variation.variation_amount || 0).toLocaleString()} ({variation.variation_percent}%)</p>
        </div>
        {variation.cumulative_flag && (
          <div className="mt-2 p-2 bg-orange-100 rounded text-orange-700 text-sm">
            ⚠️ Cumulative threshold exceeded: {variation.cumulative_percent}%
          </div>
        )}
        <p className="text-sm text-gray-500 mt-2">Requires {variation.approval_level_required} approval</p>
      </div>

      <div>
        <label className="block text-sm font-medium mb-2">Notes</label>
        <Textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Add approval notes..."
          rows={3}
        />
      </div>

      <div>
        <label className="block text-sm font-medium mb-2">Partial Approval Amount (if applicable)</label>
        <Input
          type="number"
          value={partialAmount}
          onChange={(e) => setPartialAmount(parseFloat(e.target.value) || 0)}
        />
      </div>

      <div className="flex gap-2">
        <Button 
          className="flex-1 bg-green-600 hover:bg-green-700"
          onClick={() => onApprove('approve', notes)}
        >
          <CheckCircle className="w-4 h-4 mr-2" /> Approve Full
        </Button>
        <Button 
          variant="outline"
          className="flex-1 border-yellow-500 text-yellow-600 hover:bg-yellow-50"
          onClick={() => onApprove('partial_approve', notes, partialAmount)}
        >
          <Percent className="w-4 h-4 mr-2" /> Partial
        </Button>
        <Button 
          variant="outline"
          className="flex-1 border-red-500 text-red-600 hover:bg-red-50"
          onClick={() => onApprove('reject', notes)}
        >
          <XCircle className="w-4 h-4 mr-2" /> Reject
        </Button>
      </div>
    </div>
  );
};

// Create Variation Form Component
const CreateVariationForm = ({ jobs, token, onSuccess }) => {
  const [formData, setFormData] = useState({
    job_id: '',
    job_type: 'ppm',
    variation_type: 'additional_works',
    title: '',
    description: '',
    reason: '',
    original_contract_value: 0,
    variation_amount: 0,
    new_contract_value: 0,
    time_impact_days: 0
  });
  const [submitting, setSubmitting] = useState(false);

  const variationTypes = [
    { value: 'additional_works', label: 'Additional Works' },
    { value: 'scope_change', label: 'Scope Change' },
    { value: 'price_increase', label: 'Price Increase' },
    { value: 'time_extension', label: 'Time Extension' },
    { value: 'unforeseen_conditions', label: 'Unforeseen Conditions' },
    { value: 'client_request', label: 'Client Request' }
  ];

  useEffect(() => {
    setFormData(prev => ({
      ...prev,
      new_contract_value: prev.original_contract_value + prev.variation_amount
    }));
  }, [formData.original_contract_value, formData.variation_amount]);

  const handleSubmit = async () => {
    if (!formData.job_id || !formData.title || !formData.reason) {
      toast.error('Please fill in required fields');
      return;
    }

    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/variations/`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      if (res.ok) {
        toast.success('Variation created successfully');
        onSuccess();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to create variation');
      }
    } catch (error) {
      toast.error('Error creating variation');
    } finally {
      setSubmitting(false);
    }
  };

  const variationPercent = formData.original_contract_value > 0 
    ? ((formData.variation_amount / formData.original_contract_value) * 100).toFixed(2) 
    : 0;

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium mb-1">Job *</label>
          <Select value={formData.job_id} onValueChange={(v) => setFormData(prev => ({ ...prev, job_id: v }))}>
            <SelectTrigger>
              <SelectValue placeholder="Select job" />
            </SelectTrigger>
            <SelectContent>
              {jobs.map(job => (
                <SelectItem key={job.id} value={job.id}>
                  {job.job_number || job.task_name} - {job.park_name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Variation Type *</label>
          <Select value={formData.variation_type} onValueChange={(v) => setFormData(prev => ({ ...prev, variation_type: v }))}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {variationTypes.map(t => (
                <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium mb-1">Title *</label>
        <Input
          value={formData.title}
          onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
          placeholder="Variation title"
        />
      </div>

      <div>
        <label className="block text-sm font-medium mb-1">Reason *</label>
        <Textarea
          value={formData.reason}
          onChange={(e) => setFormData(prev => ({ ...prev, reason: e.target.value }))}
          placeholder="Why is this variation needed?"
          rows={2}
        />
      </div>

      <div>
        <label className="block text-sm font-medium mb-1">Description</label>
        <Textarea
          value={formData.description}
          onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
          placeholder="Detailed description..."
          rows={2}
        />
      </div>

      <div className="grid grid-cols-3 gap-4">
        <div>
          <label className="block text-sm font-medium mb-1">Original Contract Value</label>
          <Input
            type="number"
            value={formData.original_contract_value}
            onChange={(e) => setFormData(prev => ({ ...prev, original_contract_value: parseFloat(e.target.value) || 0 }))}
          />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Variation Amount</label>
          <Input
            type="number"
            value={formData.variation_amount}
            onChange={(e) => setFormData(prev => ({ ...prev, variation_amount: parseFloat(e.target.value) || 0 }))}
          />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Time Impact (days)</label>
          <Input
            type="number"
            value={formData.time_impact_days}
            onChange={(e) => setFormData(prev => ({ ...prev, time_impact_days: parseInt(e.target.value) || 0 }))}
          />
        </div>
      </div>

      <div className="bg-gray-50 rounded-lg p-4 space-y-2">
        <div className="flex justify-between">
          <span>Original Contract</span>
          <span>£{formData.original_contract_value.toLocaleString()}</span>
        </div>
        <div className="flex justify-between text-orange-600">
          <span>Variation ({variationPercent}%)</span>
          <span>+£{formData.variation_amount.toLocaleString()}</span>
        </div>
        <div className="flex justify-between font-bold text-lg border-t pt-2">
          <span>New Contract Value</span>
          <span className="text-[#1F7A78]">£{formData.new_contract_value.toLocaleString()}</span>
        </div>
      </div>

      <Button onClick={handleSubmit} disabled={submitting} className="w-full bg-[#1F7A78] hover:bg-[#005f5f]">
        {submitting ? 'Creating...' : 'Create Variation'}
      </Button>
    </div>
  );
};

export default VariationsPage;
